import { useMemo } from 'react';
import { WORK_DAYS } from 'entities/teacher-workload/form/model/work-days-config';
import {
  applyWorkDayModeChange,
  createDefaultWorkDaysForm,
} from 'entities/teacher-workload/form/lib/workDaysFormApi';
import { USER_TIME_FORMAT } from 'shared/constants/date';
import type { SegmentedValue } from 'antd/lib/segmented';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { TeacherWorkloadWorkDayFormRow } from 'entities/teacher-workload/form/types';
import type { WorkDaysScheduleFieldProps } from './types';
import { WORK_DAY_TABLE_ROWS } from './config';
import { getWorkDaysColumns } from './columns';

export const useWorkDaysScheduleField = ({ value, onChange }: WorkDaysScheduleFieldProps) => {
  const rows = value && value.length === WORK_DAYS.length ? value : createDefaultWorkDaysForm();

  const patchRow = (index: number, patch: Partial<TeacherWorkloadWorkDayFormRow>) => {
    const next = [...rows];
    next[index] = { ...next[index], ...patch };
    onChange?.(next);
  };

  const setMode = (index: number, mode: SegmentedValue) => {
    const row = rows[index];
    if (!row) return;
    patchRow(index, applyWorkDayModeChange(row, mode as TeacherWorkloadWorkDayFormRow['mode']));
  };

  const setTimeRange = (index: number, range: RangePickerProps['value'] | null) => {
    if (!range || !range[0] || !range[1]) {
      patchRow(index, { startTime: '', endTime: '' });
      return;
    }
    patchRow(index, {
      startTime: range[0].format(USER_TIME_FORMAT),
      endTime: range[1].format(USER_TIME_FORMAT),
    });
  };

  const columns = useMemo(
    () =>
      getWorkDaysColumns({
        rows,
        onModeChange: setMode,
        onTimeRangeChange: setTimeRange,
      }),
    [rows, onChange],
  );

  return {
    state: {
      columns,
      dataSource: WORK_DAY_TABLE_ROWS,
    },
  };
};
